import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { addDays, addMonths } from 'date-fns';
import { Event, User } from 'microsoft-graph';
import React, {
  createContext, MouseEventHandler, useContext, useEffect, useMemo, useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { findIana } from 'windows-iana';
import { ClinicsEnum, ClinicStatesEnum} from './common/enums';
import { preParseISOs } from './common/utils';
import config from './Config';
import { getUser, getUserCalendar } from './GraphService';


export interface AppUser {
  displayName?: string,
  email?: string,
  avatar?: string,
  timeZone?: string,
  timeFormat?: string,
  localAccountId?: string,
};

export interface AppError {
  message: string,
  debug?: string
};

export interface AppSettings {
  redGreenMode: boolean,
}

export interface PreParsedEvent extends Omit<Event, 'start' | 'end'>  {
  startDateUTCMilliseconds: number,
  endDateUTCMilliseconds: number
  chairs?: number,
  isHygiene? : boolean
  clinicName? : ClinicsEnum
  excelMessage?: String
}

export interface ExcelEvent extends PreParsedEvent {
  clinicName : ClinicsEnum
  chairs: number
  state: ClinicStatesEnum
  excelMessage? : String
}

type AppContext = {
  user?: AppUser;
  error?: AppError;
  settings: AppSettings;
  toggleRedGreenMode?: () => void;
  successMessage?: AppError,
  signIn?: (redirectRoute?: string) => Promise<User>;
  signOut?: MouseEventHandler<HTMLElement>;
  displayError?: Function;
  clearError?: Function;
  displaySuccessMessage?: Function;
  clearSuccessMessage?: Function;
  authProvider?: AuthCodeMSALBrowserAuthenticationProvider;
  events?: PreParsedEvent[],
  queryStartDate?: Date
  queryEndDate?: Date
}

const appContext = createContext<AppContext>({
  user: undefined,
  error: undefined,
  settings: {
    redGreenMode: false,
  },
  toggleRedGreenMode: undefined,
  successMessage: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  displaySuccessMessage: undefined,
  clearSuccessMessage: undefined,
  authProvider: undefined,
  events: undefined,
  queryStartDate: undefined,
  queryEndDate: undefined,
});

export function useAppContext(): AppContext {
  return useContext(appContext);
}

interface ProvideAppContextProps {
  children: React.ReactNode;
}

export default function ProvideAppContext({ children }: ProvideAppContextProps) {
  const auth: AppContext = useProvideAppContext();

  return (
    <appContext.Provider value={auth}>
      {children}
    </appContext.Provider>
  );
}

function useProvideAppContext() {
  const [user, setUser] = useState<AppUser | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const [settings, setSettings] = useState<AppSettings>(Object.assign({ redGreenMode: false },
    JSON.parse(localStorage.getItem('settings') || '{}')));
  const [successMessage, setSuccessMessage] = useState<AppError | undefined>(undefined);
  const [events, setEvents] = useState<PreParsedEvent[] | undefined>(undefined)
  //Allowable scheduling time. (The school builds the schedule live, but it is not reliable until it is confirmed "released")
  const QUERY_START_DATE = addDays(new Date(), -1);
  const QUERY_END_DATE = addMonths(new Date(), 4);

  function toggleRedGreenMode() {
    setSettings(prevState => {
      const newSettings = {
        ...prevState,
        redGreenMode: !prevState.redGreenMode
      };

      localStorage.setItem('settings', JSON.stringify(newSettings) );

      return newSettings;
    })
  }

  useEffect(() => {
    const checkUser = async () => {
      if (!user) {
        try {
          // Check if user is already signed in
          const account = msal.instance.getActiveAccount();
          if (account) {
            // Get the user from Microsoft Graph
            const user = await getUser(authProvider);

            setUser({
              displayName: user.displayName || '',
              email: user.mail || user.userPrincipalName || '',
              timeFormat: user.mailboxSettings?.timeFormat || '',
              timeZone: user.mailboxSettings?.timeZone || 'UTC',
              localAccountId: user.id || '',
            });
          }
        } catch (err) {
          //@ts-ignore
          displayError(err.message);
        }
      }
    };
    checkUser();
  });
  
  const displayError = (message: string, debug?: string) => {
    setError({ message, debug });
  }

  const clearError = () => {
    setError(undefined);
  }

  const displaySuccessMessage = (message: string, debug?: string) => {
    setSuccessMessage({ message, debug });
  }

  const clearSuccessMessage = () => {
    setSuccessMessage(undefined);
  }
  const msal = useMsal();
    // Used by the Graph SDK to authenticate API calls
  const authProvider = useMemo(()=>new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance as PublicClientApplication,
    {
      account: msal.instance.getActiveAccount()!,
      scopes: config.scopes,
      interactionType: InteractionType.Popup
    }
  ),[msal.instance]);

  const history = useHistory();
  const signIn = async (redirectRoute?: string) => {
    await msal.instance.loginPopup({
      scopes: config.scopes,
      prompt: 'select_account'
    });

    if (redirectRoute) {
      history.push(redirectRoute)
    }

    // Get the user from Microsoft Graph
    const user = await getUser(authProvider);

    setUser({
      displayName: user.displayName || '',
      email: user.mail || user.userPrincipalName || '',
      timeFormat: user.mailboxSettings?.timeFormat || '',
      timeZone: user.mailboxSettings?.timeZone || 'UTC'
    });
    return user
  };

  const signOut = async () => {
    await msal.instance.logoutPopup();
    setUser(undefined);
  };

  useEffect(() => {
    const getCalenderEvents = async () => {
      const ianaTimeZones = findIana(user!.timeZone!);
      //Remap the events, parsing in advance datetime to date objects
      let events = preParseISOs(await getUserCalendar(authProvider, ianaTimeZones[0].valueOf(),QUERY_START_DATE, QUERY_END_DATE))
      setEvents(events);
    };
    
    if(user){
      getCalenderEvents()
    }
  },[user, authProvider]);
 

  return {
    user,
    error,
    settings,
    toggleRedGreenMode,
    successMessage,
    signIn,
    signOut,
    displayError,
    clearError,
    displaySuccessMessage,
    clearSuccessMessage,
    authProvider,
    events
  };
}